import { Trash2, Sparkles } from 'lucide-react';
import type { InventoryStats as StatsType, InventoryFlagStats } from '../../types/inventory';

interface InventoryStatsProps {
    stats: StatsType | null;
    flagStats: InventoryFlagStats | null;
    total: number;
    onFlagTileClick?: (flagType: 'ToBeDestroyed' | 'NewProduct') => void;
}

function formatEUR(value: number): string {
    return new Intl.NumberFormat('de-CH', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

export function InventoryStats({ stats, flagStats, total, onFlagTileClick }: InventoryStatsProps) {
    const class5 = stats?.byClass.find(c => c.inventoryClass === 5);
    const class6 = stats?.byClass.find(c => c.inventoryClass === 6);

    const statCards = [
        { label: 'Total Items', value: total.toLocaleString('de-CH'), color: 'text-gray-900' },
        { label: 'Stock Value', value: stats ? formatEUR(stats.totalStockValueEUR) : '-', color: 'text-blue-600' },
        { label: 'Class 5 (> 24 Mts)', value: class5 ? `${class5.itemCount} / ${formatEUR(class5.stockValueEUR)}` : '-', color: 'text-orange-600' },
        { label: 'Class 6 (No Mover)', value: class6 ? `${class6.itemCount} / ${formatEUR(class6.stockValueEUR)}` : '-', color: 'text-red-600' },
    ];

    return (
        <>
            {statCards.map(stat => (
                <div key={stat.label} className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                    <p className="text-xs font-medium text-gray-500">{stat.label}</p>
                    <p className={`mt-1 text-lg font-semibold ${stat.color}`}>{stat.value}</p>
                </div>
            ))}
            {/* To Be Destroyed KPI */}
            <div
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 cursor-pointer hover:bg-red-100 transition-colors"
                onClick={() => onFlagTileClick?.('ToBeDestroyed')}
            >
                <p className="flex items-center gap-1 text-xs font-medium text-red-600">
                    <Trash2 className="h-3.5 w-3.5" /> To Be Destroyed
                </p>
                <p className="mt-1 text-lg font-semibold text-red-700">
                    {flagStats ? `${flagStats.toBeDestroyed.count} / ${formatEUR(flagStats.toBeDestroyed.value)}` : '-'}
                </p>
            </div>
            {/* New Products KPI */}
            <div
                className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => onFlagTileClick?.('NewProduct')}
            >
                <p className="flex items-center gap-1 text-xs font-medium text-blue-600">
                    <Sparkles className="h-3.5 w-3.5" /> New Products
                </p>
                <p className="mt-1 text-lg font-semibold text-blue-700">
                    {flagStats ? flagStats.newProduct.count.toLocaleString('de-CH') : '-'}
                </p>
            </div>
        </>
    );
}
